import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  TemplateRef,
  computed,
  inject,
  output,
  signal,
  viewChild,
  DestroyRef,
  input,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
  WarningIconComponent,
  ClockIconComponent,
  ViewIconComponent,
} from '@kuiper/icons';
import {
  ALL_PARTICIPATED_ARCHIVE_ACTION,
  ALL_PARTICIPATED_FILTER_OPTIONS,
  ALL_PARTICIPATED_INSIGHTS,
  ALL_PARTICIPATED_ON_HOLD_COLUMNS,
  ALL_PARTICIPATED_ON_HOLD_TABLE_FILTER_TABS,
  ALL_PARTICIPATED_RESTORE_ACTION,
  ALL_PARTICIPATED_VIEW_DETAILS_ACTION,
} from './all-participated.constants';
import { COLUMN_IDENTIFIERS } from '../../const';
import { FilterTableColByFeatFlagPipe } from '../../../../common/pipes/filter-table-col-by-featFlag/filter-table-col-by-feat-flag.pipe';
import { isFeatureFlaggedColumnVisible } from '../../../../common/helpers/feature-flagged-table-columns/feature-flagged-table-columns.helper';
import { RelatedRequestsDropdownComponent } from '../../../../../ui-components/lib/related-requests-dropdown/related-requests-dropdown.component';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { AvatarComponent } from '../../../../../ui-components/lib/avatar/avatar.component';
import { PermissionsDirective } from '../../../../../ui-components/directives/permissions/permissions.directive';
import { SearchService } from '../../../../../ui-components/lib/search-field/search.service';
import { BadgeComponent } from '../../../../../ui-components/lib/badge/badge.component';
import { ToasterService } from '../../../../../ui-components/services/toast/toast.service';
import { ExportService } from '../../../../../ui-components/services/export/export.service';
import { TruncatePipe } from '../../../../../ui-components/pipes/truncate/truncate.pipe';
import { ModalServiceService } from '../../../../../ui-components/lib/modal/modal-service.service';
import { TrimTooltipDirective } from '../../../../../ui-components/directives/trim-tooltip/trim-tooltip.directive';
import { UserDropdownViewComponent } from '../../../../../ui-components/lib/user-dropdown-view/user-dropdown-view.component';
import { FilterOption } from '../../../../../ui-components/lib/filter-dropdown/filter-dropdown.model';
import { FilterMapping } from '../../../../../ui-components/lib/filter-dropdown/filter-mapping.enum';
import { MyInboxService } from '../../../../common/services/my-inbox/my-inbox.service';
import { SlaDirective } from '../../../../common/directives/sla/sla.directive';
import { ActionListComponent } from '../../../../common/components/action-list/action-list.component';
import { PermissionsService } from '../../../../common/services/permissions/permissions.service';
import { ArchiveConfirmationPopupComponent } from '../../../../common/components/archive-confirmation-popup/archive-confirmation-popup.component';
import { RequestCreatorComponent } from '../../../../common/components/request-creator/request-creator.component';
import { TenantService } from '../../../../common/services/tenant/tenant.service';
import { TenantPathPipe } from '../../../../common/pipes/tenant-path/tenant-path.pipe';
import { FeatureFlagsService } from '../../../../common/services/feature-flags/feature-flags.service';
import { StepStatusPipe } from '../../../../common/pipes/step-status/step-status.pipe';
import {
  filterQueryParamName,
  TableColumns,
  TableFilterTab,
} from '../../../../../ui-components/lib/table/table.model';
import { RequestTypeEnum } from '../../../../common/enums/request-type';
import { StepStatusEnum } from '../../../../common/enums/step-status';
import { AllParticipatedRequest } from '../../../../common/models/all-participated.model';
import { Insight } from '../../../../common/models/insight.model';
import { AppPermissions } from '../../../../common/constants/app-permissions';
import { ActionList } from '../../../../common/models/action-list.model';
import {
  ApiResponse,
  Meta,
} from '../../../../common/models/api-response.model';
import { EMPLOYEE_ROUTES } from '../../../../common/constants/employee.routing.constants';
import { FEATURE_FLAGS } from '../../../../common/constants/feature-flags.constants';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FilteredTableComponent } from '../../../../common/components/filtered-table/filtered-table.component';
import { FilteredTablePayload } from '../../../../common/components/filtered-table/filtered-table.model';
import { Permission } from '../../../../common/models/permission.model';
import { RequestSummaryCellComponent } from '../../../../common/components/request-summary-cell/request-summary-cell.component';
import { RequestSummaryTemplatesPipe } from '../../../../common/pipes/request-summary-templates/request-summary-templates.pipe';
import { mergeRequestSummaryColumns } from '../../../../common/helpers/request-summary-column/request-summary-column.helper';

@Component({
  selector: 'lib-all-participated',
  imports: [
    RequestSummaryCellComponent,
    RequestSummaryTemplatesPipe,
    DatePipe,
    BadgeComponent,
    StepStatusPipe,
    SlaDirective,
    TranslocoPipe,
    AvatarComponent,
    TruncatePipe,
    UserDropdownViewComponent,
    ActionListComponent,
    PermissionsDirective,
    TrimTooltipDirective,
    RouterLink,
    ReactiveFormsModule,
    WarningIconComponent,
    ClockIconComponent,
    ViewIconComponent,
    ArchiveConfirmationPopupComponent,
    RequestCreatorComponent,
    RouterLink,
    TenantPathPipe,
    FilterTableColByFeatFlagPipe,
    FilteredTableComponent,
    RelatedRequestsDropdownComponent,
  ],
  templateUrl: './all-participated.component.html',
  styleUrl: './all-participated.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class AllParticipatedComponent implements OnInit {
  readonly EMPLOYEE_ROUTES = EMPLOYEE_ROUTES;
  readonly RequestTypeEnum = RequestTypeEnum;
  readonly appPermissions = AppPermissions;
  protected readonly COLUMN_IDENTIFIERS = COLUMN_IDENTIFIERS;

  private readonly modalService = inject(ModalServiceService);
  private readonly myInboxService = inject(MyInboxService);
  private readonly transloco = inject(TranslocoService);
  protected readonly parallelStepStatus = StepStatusEnum;
  private readonly router = inject(Router);
  private readonly permissionsService = inject(PermissionsService);
  private readonly toasterService = inject(ToasterService);
  private readonly searchService = inject(SearchService);
  private readonly featureFlagService = inject(FeatureFlagsService);
  /** UI_Version_2: ID, workflow type and subject share one Request column. */
  protected readonly mergeRequestColumns = signal(
    this.featureFlagService.getFeatureFlagValue(
      FEATURE_FLAGS.TABLE.ui_enhancement
    )
  );
  readonly exportService = inject(ExportService);
  private readonly tenantService = inject(TenantService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly showParentTasksColumn = signal(
    this.featureFlagService.getFeatureFlagValue(
      FEATURE_FLAGS.INBOX.parent_wf_column
    )
  );

  protected readonly showRelatedRequestsColumn = signal(
    this.featureFlagService.getFeatureFlagValue(
      FEATURE_FLAGS.WORKFLOWS.parent_and_child_approach
    )
  );

  /******** pagination ********/
  meta = signal<Meta | undefined>(undefined);

  /*********** modal *********/
  archiveModal = viewChild<TemplateRef<unknown>>('archive');
  restoreModal = viewChild<TemplateRef<unknown>>('restore');
  selectedRequestId = signal<number>(0);

  /*********** table  *********/
  insights = signal<Insight[]>(ALL_PARTICIPATED_INSIGHTS);
  isLoading = signal<boolean>(false);
  dataSource = signal<AllParticipatedRequest[]>([]);
  readonly columns = computed<TableColumns[]>(() =>
    mergeRequestSummaryColumns(
      ALL_PARTICIPATED_ON_HOLD_COLUMNS,
      this.mergeRequestColumns()
    )
      .filter(col =>
        isFeatureFlaggedColumnVisible(col.header, {
          showParentTasksColumn: this.showParentTasksColumn(),
          showRelatedRequestsColumn: this.showRelatedRequestsColumn(),
        })
      )
      .map(column => {
        return {
          ...column,
          header: this.transloco.translate(column.header),
          showViewIcon: column.showViewIcon
            ? (row: AllParticipatedRequest) =>
                column.showViewIcon(
                  row,
                  this.permissionsService,
                  this.appPermissions
                )
            : undefined,
          viewIconRouterLink: column.viewIconRouterLink
            ? (row: AllParticipatedRequest) =>
                column.viewIconRouterLink(row, this.tenantService)
            : undefined,
        };
      })
  );
  tableFilterTabs = signal<TableFilterTab[]>(
    ALL_PARTICIPATED_ON_HOLD_TABLE_FILTER_TABS.map(tab => ({
      ...tab,
      label: this.transloco.translate(tab.label),
    }))
  );
  activeTableFilterTab = computed(
    () => this.payload().tab ?? this.tableFilterTabs()[0]
  );

  viewDetailsAction: ActionList = {
    ...ALL_PARTICIPATED_VIEW_DETAILS_ACTION,
    label: this.transloco.translate(ALL_PARTICIPATED_VIEW_DETAILS_ACTION.label),
  };

  archiveAction: ActionList = {
    ...ALL_PARTICIPATED_ARCHIVE_ACTION,
    label: this.transloco.translate(ALL_PARTICIPATED_ARCHIVE_ACTION.label),
  };

  restoreAction: ActionList = {
    ...ALL_PARTICIPATED_RESTORE_ACTION,
    label: this.transloco.translate(ALL_PARTICIPATED_RESTORE_ACTION.label),
  };

  counterRefresh = output<string>();
  actionSourceTabName = input<string>();

  filterOptions: FilterOption[] = ALL_PARTICIPATED_FILTER_OPTIONS.map(
    option => ({
      ...option,
      label: this.transloco.translate(option.label),
      placeholder: option.placeholder
        ? this.transloco.translate(option.placeholder)
        : '',
    })
  );

  filterMapping: Record<string, string> = {
    workflowTypes: FilterMapping.WORKFLOW_TYPES_FLOWS,
    creationDateStart: FilterMapping.CREATED_AFTER_FLOWS,
    creationDateEnd: FilterMapping.CREATED_BEFORE_FLOWS,
    projectIds: FilterMapping.PROJECT_IDS,
    [filterQueryParamName]: FilterMapping.PROJECT_STATE,
  };

  payload = signal<FilteredTablePayload>({
    page: 1,
    size: 25,
    sortColumn: {
      key: '',
      value: 'asc',
    },
    search: '',
    filters: {},
    tab: this.tableFilterTabs()[0],
  });

  ngOnInit(): void {
    this.getInsights();
  }

  getInsights() {
    const keys = [
      StepStatusEnum.INPROGRESS,
      StepStatusEnum.COMPLETED,
      StepStatusEnum.REJECTED,
      StepStatusEnum.ON_HOLD,
    ];
    this.myInboxService
      .getAllParticipatedInsight()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: { data: Insight[] }) => {
          keys.forEach((key, index) => {
            this.insights()[index].value =
              res.data
                .find(
                  (insight: Insight) =>
                    insight.key === key ||
                    (key === StepStatusEnum.INPROGRESS &&
                      insight.key === 'inprogress')
                )
                ?.value?.toString() ?? '0';
          });
          this.insights.set([...this.insights()]);
        },
      });
  }

  checkCalledApi() {
    return this.checkReturnedObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: ApiResponse<AllParticipatedRequest>) =>
          this.handleAllParticipatedRequestsSuccess(res),
        error: () => {
          this.isLoading.set(false);
        },
      });
  }

  checkReturnedObservable = () => {
    const { tab } = this.payload();
    return tab?.key === 'archived'
      ? this.archiveRequests$()
      : this.allParticipatedRequests$();
  };

  /* Requests Opts */
  allParticipatedRequests$() {
    this.isLoading.set(true);
    this.meta.set(undefined);

    const { page, size, sortColumn, search, filters } = this.payload();

    if (filters[FilterMapping.PROJECT_STATE] === 'all')
      delete filters[FilterMapping.PROJECT_STATE];

    return this.myInboxService.getAllParticipated(
      page,
      size,
      sortColumn,
      this.searchService.checkSearchLength(search),
      filters
    );
  }

  archiveRequests$() {
    this.isLoading.set(true);
    this.meta.set(undefined);

    const { page, size, sortColumn, search, filters } = this.payload();
    delete filters[FilterMapping.PROJECT_STATE];

    return this.myInboxService.getFilteredArchive(
      page,
      size,
      'assigned_to_me',
      sortColumn,
      0,
      1,
      this.searchService.checkSearchLength(search),
      filters
    );
  }

  handleAllParticipatedRequestsSuccess(
    res: ApiResponse<AllParticipatedRequest>
  ) {
    this.isLoading.set(false);
    res.data.forEach((element: AllParticipatedRequest) => {
      element.assignees = element.current_assignees.map(
        assignee => assignee.data
      );
      element.assigneeType = element.current_assignees.map(
        assignee => assignee.type
      );
    });
    this.dataSource.set(res.data);
    this.meta.set(res.meta);
  }

  /* Table Actions */
  viewRequestDetails(request: AllParticipatedRequest) {
    this.router.navigate([
      this.tenantService.tenantPath(
        `${EMPLOYEE_ROUTES.MY_INBOX.REQUEST}/${RequestTypeEnum.VIEW}/${request.id}`
      ),
    ]);
  }

  getStatusActionList(
    status: string,
    permissions: Permission[],
    requestId: number
  ) {
    const viewDetailsActionWithId: ActionList = {
      ...this.viewDetailsAction,
      routerLinks: [
        this.tenantService.tenantPath(
          `${EMPLOYEE_ROUTES.MY_INBOX.REQUEST}/${RequestTypeEnum.VIEW}/${requestId.toString()}`
        ),
      ],
    };

    if (
      (status === 'completed' || status === 'rejected') &&
      this.permissionsService.checkPermission(
        [this.appPermissions.workflow.archive],
        permissions
      )
    ) {
      return [
        viewDetailsActionWithId,
        this.activeTableFilterTab().key === 'archived'
          ? this.restoreAction
          : this.archiveAction,
      ];
    } else if (
      this.permissionsService.checkPermission(
        [this.appPermissions.workflow.view],
        permissions
      )
    ) {
      return [viewDetailsActionWithId];
    }
    return [];
  }

  protected getRelatedRequestViewLink = (childFlow: { id: number }) =>
    this.tenantService.tenantPath(
      `${EMPLOYEE_ROUTES.MY_INBOX.REQUEST}/${RequestTypeEnum.VIEW}/${childFlow.id}`
    );

  actionListItemClick(option: string, request: AllParticipatedRequest) {
    this.selectedRequestId.set(request.id);
    switch (option) {
      case 'archive':
        this.openModal('archive');
        break;

      case 'restore':
        this.openModal('restore');
        break;
    }
  }

  /* Action List Actions */
  openModal(type: string) {
    let modalType;
    switch (type) {
      case 'archive':
        modalType = this.archiveModal();
        break;
      case 'restore':
        modalType = this.restoreModal();
        break;
    }
    const modal = {
      isOpen: true,
      hasCloseIcon: false,
      customizeContent: modalType,
      width: '24',
    };

    this.modalService.modalContent.set(modal);
  }

  closeModal() {
    this.modalService.modalContent.set({ isOpen: false });
  }

  archiveRequest() {
    this.isLoading.set(true);
    this.closeModal();
    this.myInboxService
      .archiveRequest(this.selectedRequestId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.checkCalledApi();
          this.getInsights();
          this.counterRefresh.emit('all_participated');
        },
        error: (error: { message?: string }) => {
          this.isLoading.set(false);
          this.toasterService.add(
            error.message ?? '',
            '',
            '',
            'error',
            true,
            true
          );
        },
      });
  }

  restoreRequest() {
    this.isLoading.set(true);
    this.closeModal();
    this.myInboxService
      .restoreRequest(this.selectedRequestId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.checkCalledApi();
          this.getInsights();
          this.counterRefresh.emit('all_participated');
        },
        error: (error: { message?: string }) => {
          this.isLoading.set(false);
          this.toasterService.add(
            error.message ?? '',
            '',
            '',
            'error',
            true,
            true
          );
        },
      });
  }

  submitExportForm(exportForm: FormGroup) {
    this.myInboxService
      .exportAllParticipated(exportForm.value)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: unknown) => {
          this.exportService.handleExportSuccess(
            response as Response,
            exportForm,
            () => this.closeModal()
          );
        },
        error: (error: { message?: string }) => {
          this.toasterService.add(
            error?.message ?? '',
            '',
            '',
            'error',
            true,
            true
          );
          this.exportService.reInitExportForm(exportForm);
          this.closeModal();
        },
      });
  }
}
